import {ChangeEvent, FC, useContext, useState} from 'react';
import {SubmitHandler, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';
import {useSetRecoilState} from 'recoil';
import {Box, Button, FormControlLabel, Stack, Switch, Tooltip} from '@mui/material';
import ControlledInput from '../../../../ui/controlled-input/ControlledInput';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import {ConfirmAction} from '../../../modals/ConfirmAction';
import {ModalContainer} from '../../../modals/ModalContainer';
import {IDeviceTypesDict, IDeviceTypesDictPostBody} from '../../../../shared/types/deviceTypesDict.types';
import {deviceTypesDictQueryByCodeTrigger, deviceTypesDictQueryTrigger} from '../../../../store/deviceTypes/atoms';
import {DictionaryService} from '../../../../api/services/dictionary.service';
import {green} from '@mui/material/colors';
import {EventBusContext} from '../../../../providers/EventBus';

interface IDeviceTypeForm {
  deviceType: IDeviceTypesDict;
  subtypes: IDeviceTypesDict[];
}

export const DeviceTypeForm: FC<IDeviceTypeForm> = ({deviceType, subtypes}) => {
  const triggerDeviceTypesQuery = useSetRecoilState(deviceTypesDictQueryTrigger);
  const triggerDeviceTypeQueryByCode = useSetRecoilState(deviceTypesDictQueryByCodeTrigger);
  const {
    control,
    handleSubmit,
    formState: {isDirty, defaultValues},
    trigger,
    setValue,
  } = useForm<IDeviceTypesDictPostBody>({
    defaultValues: {
      name: deviceType.name,
      code: deviceType.code,
      description: deviceType.description ?? '',
    },
  });
  const navigate = useNavigate();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const {bus} = useContext(EventBusContext);

  const toggleModalToDelete = () => {
    setOpen(!open);
  };

  const onSubmit: SubmitHandler<IDeviceTypesDictPostBody> = async (data) => {
    setLoading(true);

    try {
      await DictionaryService.updateDeviceType({...deviceType, ...data});

      triggerDeviceTypeQueryByCode((v) => v + 1);
      triggerDeviceTypesQuery((v) => v + 1);
      setLoading(false);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const handleDelete = async (setLoading: (bool: boolean) => void) => {
    setLoading(true);

    try {
      await DictionaryService.deleteDeviceType(deviceType.code);

      triggerDeviceTypesQuery((v) => v + 1);
      setTimeout(() => {
        navigate('/dictionaries/deviceTypes');
      }, 0);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  const handleToggleInputMode = (e: ChangeEvent<HTMLInputElement>) => {
    setChecked(e.target.checked);
  };

  const handleCancelInputMode = () => {
    setChecked(false);

    Object.entries({...defaultValues}).forEach((item) => setValue(item[0] as keyof IDeviceTypesDictPostBody, item[1]));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack sx={{mt: 1.5}} direction="column" spacing={3}>
        <Stack direction="row">
          <FormControlLabel
            control={<Switch checked={checked} onChange={handleToggleInputMode} />}
            label="Редактировать"
          />
        </Stack>
        <Stack direction="row" spacing={2}>
          <Tooltip title={deviceType.name}>
            <Box>
              <ControlledInput
                control={control}
                name="name"
                disabled={!checked || loading}
                label="Название"
                placeholder="Название"
                size="small"
                rules={{
                  required: 'Обязательно для заполнения',
                }}
              />
            </Box>
          </Tooltip>
          <Tooltip title={deviceType.code}>
            <Box>
              <ControlledInput control={control} name="code" size="small" label="Код типа" disabled />
            </Box>
          </Tooltip>
        </Stack>
        <ControlledInput
          control={control}
          name="description"
          disabled={!checked || loading}
          rules={{
            maxLength: {
              value: 400,
              message: 'Максимум 400 символов',
            },
          }}
          label="Комментарий"
          multiline
          rows={4}
          additionalFunc={() => {
            trigger('description');
          }}
        />
        <Stack direction="row" spacing={3} sx={{justifyContent: 'flex-end', mb: '12px !important'}}>
          {checked ? (
            <>
              <Button disabled={loading} onClick={handleCancelInputMode} variant="outlined" color="error">
                Отменить
              </Button>
              <LoadingButton
                sx={{
                  backgroundColor: green[500],
                  '&:hover': {
                    backgroundColor: green[600],
                  },
                }}
                type="submit"
                loading={loading}
                variant="contained"
                color="success"
                disabled={!isDirty}
              >
                Сохранить
              </LoadingButton>
            </>
          ) : (
            <Button onClick={toggleModalToDelete} variant="contained" color="error">
              Удалить
            </Button>
          )}
        </Stack>
        {open && (
          <ModalContainer
            sideEffect={handleDelete}
            renderModal={(sideEffect, loading) => (
              <ConfirmAction
                loading={loading}
                open={open}
                handleClose={toggleModalToDelete}
                handleSideEffect={sideEffect}
                title="Подтвердите удаление"
                buttonTitle="Удалить"
              />
            )}
          />
        )}
      </Stack>
    </form>
  );
};
